import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import {
  PaymentEvent,
  PaymentMethod,
  PaymentPlan,
  PaymentProviderId,
  PaymentStatus,
  PaymentTransaction,
  SubscriptionStatus,
} from '../types';
import { PAYMENT_METHOD_META } from '../config/defaults';
import { usePaymentStore } from '../state/usePaymentStore';
import { paymentApi } from '../services/paymentApi';
import { paymentService } from './usePaymentService';
import {
  trackCheckoutStarted,
  trackPaymentInfoAdded,
  trackPurchase,
  trackPaymentFailed,
  trackPaymentCancelled,
  trackPaymentExpired,
  trackPaymentError,
  trackPaymentRetry,
} from '../services/paymentAnalytics';

/**
 * ─── usePaymentFlow ──────────────────────────────────────────────────────────
 *
 * Orchestre une tentative de paiement de bout en bout au-dessus de la machine
 * d'états : initiation, redirection, retour provider, vérification (polling),
 * activation de l'abonnement, écriture de l'historique. Les pages ne
 * contiennent que de l'affichage.
 */

const POLL_INTERVAL_MS = 2500;

/**
 * Distingue une vraie coupure réseau (aucune réponse du serveur) d'un rejet
 * MÉTIER (le serveur a répondu 4xx avec un message) et en extrait le message
 * lisible. Un 400 « Plan introuvable » ou « FlexPay a refusé… » ne doit PAS
 * s'afficher comme « Problème de connexion — vérifiez votre connexion internet ».
 */
function describeInitError(err: unknown): { message: string; networked: boolean } {
  const e = err as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
  };
  const data = e?.response?.data?.message;
  const backendMsg = Array.isArray(data) ? data.join(', ') : data;
  if (e?.response) {
    return {
      message: backendMsg || 'Le paiement a été refusé par le service de paiement.',
      networked: false,
    };
  }
  return {
    message: e?.message || 'Impossible de contacter le service de paiement.',
    networked: true,
  };
}

/**
 * Réplique la transaction dans le registre SERVEUR (best-effort) : c'est ce
 * registre que l'admin consulte pour retrouver un paiement coupé (utilisateur
 * débité mais vérification jamais aboutie) et le régler manuellement.
 * Comptes réels uniquement (le backend refuse les invités).
 */
function syncTxToServer(tx: PaymentTransaction & { planName?: string }): Promise<void> {
  const { isAuthenticated, isGuestSession } = useAuthStore.getState();
  if (!isAuthenticated || isGuestSession) return Promise.resolve();
  return paymentApi
    .saveMyTransaction(tx)
    .then(() => undefined)
    .catch(() => undefined);
}

/** Construit la transaction serveur "pending" depuis l'attempt courant. */
function buildPendingTx(): (PaymentTransaction & { planName?: string }) | null {
  const { attempt: a, plans } = usePaymentStore.getState();
  if (!a) return null;
  const plan = plans.find((p) => p.id === a.planId);
  return {
    reference: a.reference,
    providerOrderNumber: a.orderNumber,
    planId: a.planId,
    planName: plan?.name,
    amount: plan?.price ?? 0,
    currency: plan?.currency ?? 'USD',
    providerId: a.providerId,
    method: a.method,
    status: PaymentStatus.PENDING,
    createdAt: a.startedAt,
    completedAt: null,
    failureReason: null,
  };
}

export function usePaymentFlow() {
  const navigate = useNavigate();
  const attempt = usePaymentStore((s) => s.attempt);
  const plans = usePaymentStore((s) => s.plans);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  /** Écrit la transaction dans l'historique à partir de l'attempt courant. */
  const writeHistory = useCallback(
    (status: PaymentStatus, failureReason: string | null) => {
      const { attempt: a } = usePaymentStore.getState();
      if (!a) return;
      const plan = usePaymentStore.getState().plans.find((p) => p.id === a.planId);
      const tx: PaymentTransaction = {
        reference: a.reference,
        providerOrderNumber: a.orderNumber,
        planId: a.planId,
        amount: plan?.price ?? 0,
        currency: plan?.currency ?? 'USD',
        providerId: a.providerId,
        method: a.method,
        status,
        createdAt: a.startedAt,
        completedAt:
          status === PaymentStatus.SUCCESS ? new Date().toISOString() : null,
        failureReason,
      };
      usePaymentStore.getState().recordTransaction(tx);
      void syncTxToServer({ ...tx, planName: plan?.name });
    },
    [],
  );

  /** Succès confirmé : abonnement activé + facture + historique. */
  const finalizeSuccess = useCallback(() => {
    const { attempt: a, plans: allPlans, subscription: currentSub } =
      usePaymentStore.getState();
    if (!a) return;
    const plan = allPlans.find((p) => p.id === a.planId);
    if (!plan) return;

    // Renouvellement si un abonnement actif non expiré existe AVANT activation
    // (même critère que activateSubscription) → sert au taux de renouvellement.
    const isRenewal =
      !!currentSub &&
      currentSub.status === SubscriptionStatus.ACTIVE &&
      new Date(currentSub.expiresAt) > new Date();

    const { user: authUser, isAuthenticated, isGuestSession } = useAuthStore.getState();
    const { subscription, invoice } = usePaymentStore.getState().activateSubscription({
      plan,
      providerId: a.providerId,
      method: a.method,
      paymentMethodLabel: PAYMENT_METHOD_META[a.method]?.label ?? null,
      transactionReference: a.reference,
      customer: {
        name: authUser?.name ?? 'Utilisateur',
        email: authUser?.email ?? '',
      },
    });
    writeHistory(PaymentStatus.SUCCESS, null);
    trackPurchase(isRenewal); // KPI revenu + acquisition/renouvellement

    // Compte connecté (le checkout l'exige) : rattache l'abonnement au compte
    // (il survivra aux changements d'appareil / purge locale) et envoie la
    // facture par email. Best-effort : un échec réseau ne casse pas le succès.
    if (isAuthenticated && !isGuestSession) {
      void paymentApi.saveMySubscription(subscription).catch(() => {});
      void paymentApi
        .sendInvoiceEmail(invoice, subscription.paymentMethodLabel)
        .catch(() => {});
    }
  }, [writeHistory]);

  /**
   * Boucle de vérification (équivalent du polling FlexPay "check") :
   * tant que le provider répond 'pending' (callback pas encore reçu), on
   * re-vérifie ; au-delà du timeout config → EXPIRED.
   */
  const runVerification = useCallback(() => {
    stopPolling();
    const { attempt: a, config } = usePaymentStore.getState();
    if (!a?.orderNumber) return;
    const deadline =
      new Date(a.startedAt).getTime() + config.verificationTimeoutSeconds * 1000;

    const poll = async () => {
      const { attempt: current } = usePaymentStore.getState();
      if (!current?.orderNumber || current.status !== PaymentStatus.VERIFYING) return;

      try {
        const result = await paymentService.verify(
          current.providerId,
          current.orderNumber,
        );

        if (result.status === 'success') {
          usePaymentStore.getState().applyEvent(PaymentEvent.VERIFIED_OK);
          finalizeSuccess();
          return;
        }
        if (result.status === 'failed' || result.status === 'not_found') {
          usePaymentStore
            .getState()
            .applyEvent(PaymentEvent.VERIFIED_KO, { error: result.message });
          writeHistory(PaymentStatus.FAILED, result.message);
          trackPaymentFailed(result.message);
          return;
        }
        // 'pending' → callback pas encore arrivé : on continue à poller.
        if (Date.now() > deadline) {
          usePaymentStore.getState().applyEvent(PaymentEvent.TIMEOUT, {
            error:
              'La vérification a pris trop de temps. Si vous avez été débité, le paiement sera confirmé automatiquement.',
          });
          writeHistory(PaymentStatus.EXPIRED, 'Délai de vérification dépassé');
          trackPaymentExpired();
          return;
        }
        pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        usePaymentStore.getState().applyEvent(PaymentEvent.NETWORK_FAIL, {
          error: 'Connexion interrompue pendant la vérification.',
        });
        trackPaymentError('Connexion interrompue pendant la vérification.');
      }
    };

    void poll();
  }, [finalizeSuccess, stopPolling, writeHistory]);

  /** Démarre un paiement pour un plan + moyen de paiement (+ téléphone si mobile). */
  const startPayment = useCallback(
    async (plan: PaymentPlan, method: PaymentMethod, phone?: string) => {
      const provider = paymentService.resolveProviderFor(method);
      if (!provider) return;

      const reference = paymentService.createReference(plan.id);
      const store = usePaymentStore.getState();
      store.beginAttempt({
        reference,
        planId: plan.id,
        providerId: provider.id,
        method,
        phone,
      });
      store.applyEvent(PaymentEvent.INITIATE);
      trackCheckoutStarted(); // KPI : tentative de paiement (dénominateur)

      const authUser = useAuthStore.getState().user;
      try {
        const { initiation } = await paymentService.initiate({
          plan,
          method,
          reference,
          phone,
          user: {
            id: authUser?.id,
            name: authUser?.name ?? 'Utilisateur',
            email: authUser?.email ?? '',
          },
        });

        if (initiation.kind === 'redirect') {
          usePaymentStore.getState().applyEvent(PaymentEvent.REDIRECT, {
            orderNumber: initiation.orderNumber,
          });
          trackPaymentInfoAdded();
          // Enregistre la transaction "pending" côté serveur AVANT de quitter
          // la page : si l'utilisateur est débité puis coupé, l'admin la
          // retrouvera. Borné à 2.5s pour ne pas retarder la redirection.
          const pending = buildPendingTx();
          if (pending) {
            await Promise.race([
              syncTxToServer(pending),
              new Promise((r) => setTimeout(r, 2500)),
            ]);
          }
          // URL interne (mock) → router ; URL externe (page hébergée) → location.
          if (initiation.url.startsWith('/')) {
            navigate(initiation.url);
          } else {
            window.location.assign(initiation.url);
          }
          return;
        }

        if (initiation.kind === 'push_sent') {
          usePaymentStore.getState().applyEvent(PaymentEvent.PUSH_SENT, {
            orderNumber: initiation.orderNumber,
          });
          trackPaymentInfoAdded();
          const pending = buildPendingTx();
          if (pending) void syncTxToServer(pending);
          // Le client valide le push sur son téléphone : on passe en
          // vérification et on poll le statut jusqu'au succès / échec / timeout.
          usePaymentStore.getState().applyEvent(PaymentEvent.VERIFY);
          runVerification();
          return;
        }

        usePaymentStore.getState().applyEvent(
          initiation.retriable ? PaymentEvent.NETWORK_FAIL : PaymentEvent.VERIFIED_KO,
          { error: initiation.message },
        );
        if (initiation.retriable) trackPaymentError(initiation.message);
        else trackPaymentFailed(initiation.message);
      } catch (err) {
        const { message, networked } = describeInitError(err);
        if (networked) {
          // Vraie coupure réseau (aucune réponse serveur) → écran « Problème de
          // connexion » + Réessayer.
          usePaymentStore.getState().applyEvent(PaymentEvent.NETWORK_FAIL, { error: message });
          trackPaymentError(message);
        } else {
          // Le serveur a répondu par un rejet (400 : plan/numéro invalide,
          // FlexPay a refusé…) → on montre la VRAIE raison, pas « vérifiez votre
          // connexion internet ».
          usePaymentStore.getState().applyEvent(PaymentEvent.VERIFIED_KO, { error: message });
          trackPaymentFailed(message);
        }
      }
    },
    [navigate, runVerification],
  );

  /**
   * À appeler sur la page de retour (/payment/return) : interprète l'issue
   * (approved / cancelled / declined) et lance la vérification si besoin.
   */
  const resumeFromReturn = useCallback(
    (params: URLSearchParams) => {
      const parsed = paymentService.parseReturn(params);
      const store = usePaymentStore.getState();
      const a = store.attempt;
      if (!parsed || !a) return;

      switch (parsed.result.outcome) {
        case 'approved':
          store.applyEvent(PaymentEvent.RETURNED_APPROVED, {
            orderNumber: parsed.result.orderNumber ?? a.orderNumber,
          });
          runVerification();
          break;
        case 'cancelled':
          store.applyEvent(PaymentEvent.RETURNED_CANCELLED);
          writeHistory(PaymentStatus.CANCELLED, 'Paiement annulé par l’utilisateur');
          trackPaymentCancelled();
          break;
        case 'declined':
          store.applyEvent(PaymentEvent.RETURNED_DECLINED, {
            error: 'Le paiement a été refusé.',
          });
          writeHistory(PaymentStatus.FAILED, 'Paiement refusé');
          trackPaymentFailed('Paiement refusé');
          break;
      }
    },
    [runVerification, writeHistory],
  );

  /** Relance après échec / annulation / erreur réseau. */
  const retry = useCallback(async () => {
    const store = usePaymentStore.getState();
    const a = store.attempt;
    if (!a) return;
    const plan = store.plans.find((p) => p.id === a.planId);
    if (!plan) return;
    trackPaymentRetry();
    store.clearAttempt();
    await startPayment(plan, a.method, a.phone);
  }, [startPayment]);

  /** Re-vérifier manuellement (après EXPIRED ou NETWORK_ERROR). */
  const verifyAgain = useCallback(() => {
    const store = usePaymentStore.getState();
    const next = store.applyEvent(PaymentEvent.VERIFY);
    if (next === PaymentStatus.VERIFYING) runVerification();
  }, [runVerification]);

  const cancel = useCallback(() => {
    stopPolling();
    const store = usePaymentStore.getState();
    store.applyEvent(PaymentEvent.CANCEL);
    writeHistory(PaymentStatus.CANCELLED, 'Paiement annulé');
    trackPaymentCancelled();
  }, [stopPolling, writeHistory]);

  const reset = useCallback(() => {
    stopPolling();
    usePaymentStore.getState().clearAttempt();
  }, [stopPolling]);

  return {
    attempt,
    plans,
    startPayment,
    resumeFromReturn,
    runVerification,
    verifyAgain,
    retry,
    cancel,
    reset,
  };
}
